import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AppConfig } from '../app.config';
import { modelDefaultValues } from './default.values';
import {
    AddRiskCalculationCommentCommand, ApproveRiskCalculationCommand, CalculationComment,
    CalculationInput, CalculationInputScreeningData, CalculationModelType,
    CalculationTempData, CalculationType, CaseWorker, ModelDefaultValues, RiskCalculationDetailView,
    RiskCalculationItem,
    SearchView,
    StartRiskCalculationCommand, UnapproveRiskCalculationCommand
} from '../contracts';

const TEMP_STORAGE_KEY = 'CalculationTempData';

@Injectable()
export class CalculationService {
    constructor(private readonly http: HttpClient) { }

    public getCalculationTempData(): CalculationTempData {
        const json = sessionStorage.getItem(TEMP_STORAGE_KEY);
        let temp = JSON.parse(json);
        if (!temp) {
            temp = new CalculationTempData();
        }
        return temp as CalculationTempData;
    }

    private setCalculationTempData(temp: CalculationTempData): void {
        const json = JSON.stringify(temp);
        sessionStorage.setItem(TEMP_STORAGE_KEY, json);
    }

    public clearCalculationTempData(): void {
        sessionStorage.removeItem(TEMP_STORAGE_KEY);
    }

    // Used for 'empty' calculations
    public createTempEmpty(locationId: string, locationName: string, locationNumber: string, calculationType: CalculationType): void {
        const temp = new CalculationTempData();
        temp.tempCalculationInput = new CalculationInput({});
        temp.tempLocationId = locationId;
        temp.tempLocationName = locationName;
        temp.tempLocationNumber = locationNumber;
        temp.tempCalculationType = calculationType;
        temp.tempScreeningData = null;
        temp.tempCalculationModel = CalculationModelType.ModelI;
        this.setCalculationTempData(temp);
    }

    // Used for V1-calculations
    public createTempV1(
        locationId: string, locationName: string, locationNumber: string,
        screeningData: CalculationInputScreeningData): void {
        const temp = new CalculationTempData();
        temp.tempCalculationInput = new CalculationInput({});
        temp.tempLocationId = locationId;
        temp.tempLocationName = locationName;
        temp.tempLocationNumber = locationNumber;
        temp.tempCalculationType = CalculationType.V1AsV2Calculation;
        temp.tempScreeningData = screeningData;
        temp.tempCalculationModel = CalculationModelType.ModelI;
        this.setCalculationTempData(temp);
    }

    // Used for 'standard' calculations created from screening results
    public createFromScreening(
        locationId: string,
        locationName: string,
        locationNumber: string,
        compoundCasNr: string,
        compoundName: string,
        screeningData: CalculationInputScreeningData,
        calculationType: CalculationType): void {
            const temp = new CalculationTempData();
            temp.tempCalculationInput = new CalculationInput({});
            temp.tempCalculationInput.contaminantCasNr = compoundCasNr;
            temp.tempCalculationInput.contaminantName = compoundName;
            temp.tempLocationId = locationId;
            temp.tempLocationName = locationName;
            temp.tempLocationNumber = locationNumber;
            temp.tempCalculationType = calculationType;
            temp.tempScreeningData = screeningData;
            temp.tempCalculationModel = CalculationModelType.ModelI;
            this.setCalculationTempData(temp);
    }

    // Used for starting a new calculation from an existing result page (same input)
    public createTempFromCalculation(
        locationId: string, locationName: string, locationNumber: string,
        calculationType: CalculationType, calculationInput: CalculationInput): void {
        const temp = new CalculationTempData();
        temp.tempLocationId = locationId;
        temp.tempLocationName = locationName;
        temp.tempLocationNumber = locationNumber;
        temp.tempCalculationInput = calculationInput;
        temp.tempCalculationType = calculationType;
        temp.tempScreeningData = null;
        temp.isCopyData = true;
        this.setCalculationTempData(temp);
    }

    public setTempCalculationModel(model: CalculationModelType): void {
        const temp = this.getCalculationTempData();
        temp.tempCalculationModel = model;
        this.setCalculationTempData(temp);
    }

    public getTempCalculationModel(): CalculationModelType {
        const temp = this.getCalculationTempData();
        return temp.tempCalculationModel;
    }

    public getTempCalculationInput(): CalculationInput {
        const temp = this.getCalculationTempData();
        return temp.tempCalculationInput;
    }

    public getTempLocationId(): string {
        const temp = this.getCalculationTempData();
        return temp.tempLocationId;
    }

    public getTempLocationName(): string {
        const temp = this.getCalculationTempData();
        return temp.tempLocationName;
    }

    public getTempLocationNumber(): string {
        const temp = this.getCalculationTempData();
        return temp.tempLocationNumber;
    }

    public getTempCalculationType(): CalculationType {
        const temp = this.getCalculationTempData();
        return temp.tempCalculationType;
    }

    public getScreeningData(): CalculationInputScreeningData {
        const temp = this.getCalculationTempData();
        return temp.tempScreeningData;
    }

    public getModelDefaultValues(model: CalculationModelType): ModelDefaultValues {
        return modelDefaultValues.get(model);
    }

    public startCalculation(locationId: string, type: CalculationType, input: CalculationInput): Observable<any> {
        const url = AppConfig.apiUrls.riskCalculations.start();
        const command = new StartRiskCalculationCommand({ locationId, calculationType: type, calculationInput: input });
        return this.http.post(url, command);
    }

    public getCalculationDetails(id: string): Observable<RiskCalculationDetailView> {
        const url = AppConfig.apiUrls.riskCalculations.getById(id);
        return this.http.get<RiskCalculationDetailView>(url);
    }

    public approveCalculation(id: string, comment: string): Observable<any> {
        const url = AppConfig.apiUrls.riskCalculations.approve(id);
        const request = new ApproveRiskCalculationCommand({ comment });
        return this.http.post<any>(url, request);
    }

    public unapproveCalculation(id: string, comment: string): Observable<any> {
        const url = AppConfig.apiUrls.riskCalculations.unapprove(id);
        const request = new UnapproveRiskCalculationCommand({ comment });
        return this.http.post<any>(url, request);
    }

    public deleteCalculation(id: string): Observable<any> {
        const url = AppConfig.apiUrls.riskCalculations.delete(id);
        return this.http.delete<any>(url);
    }

    public addCommentToCalculation(id: string, comment: string): Observable<CalculationComment> {
        const url = AppConfig.apiUrls.riskCalculations.addcomment(id);
        const request = new AddRiskCalculationCommentCommand({ text: comment });
        return this.http.post<CalculationComment>(url, request);
    }

    public setRiskCalculationsToCache(key: string, res: SearchView<RiskCalculationItem>): void {
        const json = JSON.stringify(res);
        sessionStorage.setItem(key, json);
    }

    public searchCalculationService(criteria: any): Observable<SearchView<RiskCalculationItem>> {
        const url = AppConfig.apiUrls.riskCalculations.list();

        let params = new HttpParams()
            .set('searchText', criteria.searchText ? criteria.searchText : '')
            .set('caseWorkerCvr', criteria.caseWorker ? criteria.caseWorker.cvr : '')
            .set('caseWorkerName', criteria.caseWorker ? criteria.caseWorker.name : '')
            .set('riskAssessmentStatus', criteria.riskAssessmentStatus ? criteria.riskAssessmentStatus.status : '')
            .set('minFactor', criteria.minFactor ? criteria.minFactor : '')
            .set('maxFactor', criteria.maxFactor ? criteria.maxFactor : '')
            .set('region', criteria.Region ? criteria.Region : '')
            .set('orderBy', criteria.orderBy || criteria.orderBy === 0 ? criteria.orderBy : '')
            .set('orderDescending', criteria.orderDescending ? criteria.orderDescending : false)
            .set('continuationToken', criteria.continuationToken ? criteria.continuationToken : '')
            .set('skip', criteria.skip ? criteria.skip : 0)
            .set('take', criteria.take ? criteria.take : '')
            .set('isMyCalculationOnly', criteria.isMyCalculationOnly ? criteria.isMyCalculationOnly : false);
        
            const { caseWorkerSelected } = criteria;

        if (caseWorkerSelected && caseWorkerSelected.email) {
            params = params
                .set('calculationCaseWorkerName', caseWorkerSelected.name)
                .set('calculationCaseWorkerEmail', caseWorkerSelected.email);

            if (this.isGuid(caseWorkerSelected.id)) {
                params = params
                    .set('calculationCaseWorkerId', caseWorkerSelected.id);
            }
        }

        return this.http.get<SearchView<RiskCalculationItem>>(url, { params });
    }

    private isGuid(value: string): boolean {
        if (!value) { return false; }
        const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
        return guidRegex.test(value.trim());
    }

    public getCaseWorkers(regionCode: string): Observable<any> {
        const params = new HttpParams().set('regionCode', regionCode);

        const url = AppConfig.apiUrls.riskCalculations.getCaseWorkers();

        return this.http.get<any>(url, { params });
    }
}
